import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Lazy load pages
const ShopPage = React.lazy(() => import('./pages/ShopHome').then(module => ({ default: module.MainByAnima })));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const Products = React.lazy(() => import('./pages/Products'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const ProductInfo = React.lazy(() => import('./pages/Product'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ShopLayout = React.lazy(() => import('./layouts/ShopLayout'));
const Authe = React.lazy(() => import('./pages/SignupinPage'));
const MyAccountPage = React.lazy(() => import('./pages/MyAccountPage'));
const OrderSuccessPage = React.lazy(() => import('./pages/OrderSuccessPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));


// Admin Components
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminSigninPage = React.lazy(() => import('./pages/admin/AdminSigninPage'));
const ProductsList = React.lazy(() => import('./pages/admin/products/ProductsList'));
const OrdersList = React.lazy(() => import('./pages/admin/orders/OrdersList'));
const ProductForm = React.lazy(() => import('./pages/admin/products/ProductForm'));
const ProductLiveEditor = React.lazy(() => import('./pages/admin/products/ProductLiveEditor'));
const SettingsPage = React.lazy(() => import('./pages/admin/SettingsPage'));
const CaseStudiesPage = React.lazy(() => import('./pages/CaseStudiesPage'));
const CaseStudyDetail = React.lazy(() => import('./pages/CaseStudyDetail'));
const FooterSection = React.lazy(() => import('./pages/admin/footer/FooterManagement'));

const HomepageManagement = React.lazy(() => import('./pages/admin/homepage/HomepageManagement'));
const NewsletterSubscribers = React.lazy(() => import('./pages/admin/footer/NewsletterSubscribers'));
const HomepageLiveEditor = React.lazy(() => import('./pages/admin/homepage/HomepageLiveEditor'));
const GeneralSettingsPage = React.lazy(() => import('./pages/admin/GeneralSettingsPage'));

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Admin Login - Public Route */}
                  <Route path="/admin/login" element={<AdminSigninPage />} />

                  {/* Admin Routes - Protected by admin authentication */}
                  <Route
                    path="/admin"
                    element={
                      <AdminProtectedRoute>
                        <Dashboard />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <AdminProtectedRoute>
                        <OrdersList />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products"
                    element={
                      <AdminProtectedRoute>
                        <ProductsList />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/new"
                    element={
                      <AdminProtectedRoute>
                        <ProductLiveEditor />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/:id/editor"
                    element={
                      <AdminProtectedRoute>
                        <ProductLiveEditor />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products/:id/edit"
                    element={
                      <AdminProtectedRoute>
                        <ProductForm />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* Settings Route (Categories Management) */}
                  <Route
                    path="/admin/settings"
                    element={
                      <AdminProtectedRoute>
                        <SettingsPage />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* Footer Management Route */}
                  <Route
                    path="/admin/footer"
                    element={
                      <AdminProtectedRoute>
                        <FooterSection />
                      </AdminProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/newsletter"
                    element={
                      <AdminProtectedRoute>
                        <NewsletterSubscribers />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* General Settings Route */}
                  <Route
                    path="/admin/general-settings"
                    element={
                      <AdminProtectedRoute>
                        <GeneralSettingsPage />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* NEW: Homepage Management Route */}
                  <Route
                    path="/admin/homepage"
                    element={
                      <AdminProtectedRoute>
                        <HomepageManagement />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/homepage/editor"
                    element={
                      <AdminProtectedRoute>
                        <HomepageLiveEditor />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* Shop Routes (Now wrapped in ShopLayout to provide CartContext) */}
                  <Route element={<ShopLayout />}>
                    {/* Public auth route - redirect authenticated users */}
                    <Route
                      path="/auth"
                      element={
                        <ProtectedRoute requireAuth={false}>
                          <Authe />
                        </ProtectedRoute>
                      }
                    />

                    {/* Public shopping routes */}
                    <Route path="/" element={<ShopPage />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:section" element={<Products />} />
                    <Route path="/product/:id" element={<ProductPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/case-studies" element={<CaseStudiesPage />} />
                    <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />

                    {/* Protected routes - require authentication */}
                    <Route
                      path="/my-account"
                      element={
                        <ProtectedRoute requireAuth={true}>
                          <MyAccountPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/checkout"
                      element={
                        <ProtectedRoute requireAuth={true}>
                          <CheckoutPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cart"
                      element={
                        <ProtectedRoute requireAuth={true}>
                          <CartPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Order Success Route */}
                    <Route path="/order-success/:orderId" element={<OrderSuccessPage />} />

                    {/* Legacy Routes */}
                    <Route path="/productinfo" element={<ProductInfo />} />

                    {/* Homepage section routes - must be after all other routes */}
                    <Route path="/:section" element={<ShopPage />} />
                  </Route>

                  {/* 404 Page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;